import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  getStaticOptions,
  getTemplateParameterGroupOptions,
  getTemplateParameters,
  updateTemplateParameter,
  type TemplateParameterQuery,
} from '../api/templateParameterApi'
import type { TemplateParameterUpsert } from '../types'

export const TEMPLATE_PARAMETER_KEY = 'admin-template-parameters'

/**
 * Şablon Parametresi sunucu-durumu (059).
 *
 * **`i18n.language` queryKey'in parçasıdır:** grup adı, "Statik mi?" değeri ve combo etiketleri
 * sunucuda `Accept-Language`'e göre çözülür.
 *
 * Grup **combosu** her zaman yüklenir (Parametreler sekmesinin filtresidir); grup **listesi** ise
 * yalnız Gruplar sekmesine girildiğinde çekilir (bkz. `useTemplateParamGroups`).
 *
 * **Ekleme/silme/durum mutasyonu YOKTUR** (2026-08-04 kararı): ekranda parametreler yalnız
 * düzenlenir. Sunucudaki uçlar yerinde durur ama istemci onları çağırmaz.
 */
export function useTemplateParameters(query: TemplateParameterQuery) {
  const qc = useQueryClient()
  const { i18n } = useTranslation()

  const list = useQuery({
    // `sort` anahtarın parçasıdır: kolon sıralaması sunucu-taraflıdır → değişince yeniden çekilmeli.
    queryKey: [
      TEMPLATE_PARAMETER_KEY,
      i18n.language,
      query.groupId ?? null,
      query.page,
      query.size,
      query.sort ?? null,
    ],
    queryFn: () => getTemplateParameters(query),
    placeholderData: keepPreviousData,
  })

  const groupOptions = useQuery({
    queryKey: [TEMPLATE_PARAMETER_KEY, 'group-options', i18n.language],
    queryFn: getTemplateParameterGroupOptions,
  })

  /**
   * "Statik mi?" seçenekleri — 2026-08-05'ten beri bir **seçim kaynağı değil**: alan formda
   * salt-okunur (kaydın kendi `isStaticName`'i gösterilir). İstek yalnız **"Evet" kimliğini** (`yes`
   * bayrağı) çözmek için duruyor; statik içerik alanının varlığını o belirler ve kimlik istemciye
   * gömülmez (research D14).
   */
  const staticOptions = useQuery({
    queryKey: [TEMPLATE_PARAMETER_KEY, 'static-options', i18n.language],
    queryFn: getStaticOptions,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: [TEMPLATE_PARAMETER_KEY] })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: TemplateParameterUpsert }) =>
      updateTemplateParameter(id, body),
    onSuccess: invalidate,
  })

  return {
    list,
    groupOptions,
    staticOptions,
    invalidate,
    update,
  }
}
